import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/shared/format/date-format';
import { hasCapability, useSession } from '@/app/session/session';
import { OutcomeBadge } from './AdminTags';
import { useOperationsBackups } from './admin-data';
import { DataBackupCard } from './DataBackupCard';

/**
 * The operations screen (task 23.6, R17.6, R17.10).
 *
 * A pure read of the recorded backup runs and restore verifications, so a
 * System_Administrator can see that the daily backup completed and the periodic
 * restore verification passed. Two tables: the runs (schedule, completion, covered
 * stores, the three record counts, retention window, outcome) and the
 * verifications (verified-at, the run verified, the compared counts, outcome).
 * Counts are right-aligned and tabular per UI spec §3.5.
 */
export function OperationsSection(): React.JSX.Element {
  const { t } = useTranslation();
  const query = useOperationsBackups();
  const session = useSession();
  const canExportBackup = hasCapability(session.data, 'admin:backup');
  const backupRuns = query.data?.backupRuns ?? [];
  const verifications = query.data?.restoreVerifications ?? [];

  const count = (value: number | null): string =>
    value === null ? t('admin.common.none') : String(value);
  const timestamp = (value: string | null): string =>
    value === null ? t('admin.operations.backups.pending') : formatDate(value.slice(0, 10));

  return (
    <div className="flex flex-col gap-4">
      {canExportBackup ? <DataBackupCard /> : null}
      <Card className="gap-4 p-[14px]">
        <div>
          <p className="text-[13px] font-semibold text-txt">{t('admin.operations.backups.heading')}</p>
          <p className="text-muted-foreground mt-1 text-[11px]">{t('admin.operations.hint')}</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.backups.colScheduled')}</TableHead>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.backups.colCompleted')}</TableHead>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.backups.colStores')}</TableHead>
              <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.backups.colInitiatives')}</TableHead>
              <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.backups.colMasterData')}</TableHead>
              <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.backups.colChanges')}</TableHead>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.backups.colRetainUntil')}</TableHead>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.backups.colOutcome')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isError ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground py-6 text-center text-[12.3px]">
                  {t('admin.common.loadError')}
                </TableCell>
              </TableRow>
            ) : backupRuns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground py-6 text-center text-[12.3px]">
                  {query.isLoading ? t('admin.common.loading') : t('admin.operations.backups.empty')}
                </TableCell>
              </TableRow>
            ) : (
              backupRuns.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="text-[12.3px]">{formatDate(run.scheduledAt.slice(0, 10))}</TableCell>
                  <TableCell className="text-[12.3px]">{timestamp(run.completedAt)}</TableCell>
                  <TableCell className="text-[12px] whitespace-normal">{run.coveredStores.join(', ')}</TableCell>
                  <TableCell className="text-right text-[12.3px] tabular-nums">{count(run.initiativeCount)}</TableCell>
                  <TableCell className="text-right text-[12.3px] tabular-nums">{count(run.masterDataCount)}</TableCell>
                  <TableCell className="text-right text-[12.3px] tabular-nums">{count(run.changeEventCount)}</TableCell>
                  <TableCell className="text-[12.3px]">{formatDate(run.retainUntil.slice(0, 10))}</TableCell>
                  <TableCell><OutcomeBadge outcome={run.outcome} /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Card className="gap-4 p-[14px]">
        <p className="text-[13px] font-semibold text-txt">{t('admin.operations.verifications.heading')}</p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.verifications.colVerified')}</TableHead>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.verifications.colBackup')}</TableHead>
              <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.verifications.colInitiatives')}</TableHead>
              <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.verifications.colMasterData')}</TableHead>
              <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.verifications.colChanges')}</TableHead>
              <TableHead className="text-[10.5px] uppercase">{t('admin.operations.verifications.colOutcome')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {verifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-6 text-center text-[12.3px]">
                  {query.isLoading ? t('admin.common.loading') : t('admin.operations.verifications.empty')}
                </TableCell>
              </TableRow>
            ) : (
              verifications.map((verification) => (
                <TableRow key={verification.id}>
                  <TableCell className="text-[12.3px]">{formatDate(verification.verifiedAt.slice(0, 10))}</TableCell>
                  <TableCell className="font-mono text-[12px] text-muted-foreground">{verification.backupRunId}</TableCell>
                  <TableCell className="text-right text-[12.3px] tabular-nums">{count(verification.initiativeCount)}</TableCell>
                  <TableCell className="text-right text-[12.3px] tabular-nums">{count(verification.masterDataCount)}</TableCell>
                  <TableCell className="text-right text-[12.3px] tabular-nums">{count(verification.changeEventCount)}</TableCell>
                  <TableCell><OutcomeBadge outcome={verification.outcome} /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
